import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { ChevronLeftIcon, ChevronRightIcon, LightbulbIcon } from 'lucide-react';

import { carouselPosition } from '../../model/carousel';

interface InsightCarouselProps {
  title: string;
  count: number;
  index: number;
  onIndexChange: (next: number) => void;
  children: ReactNode;
}

const navButtonClassName =
  'flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors duration-fast ease-out hover:bg-accent';

/** The slide's text as one sentence: `textContent` would run adjacent elements together. */
const readableText = (node: Node): string => {
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  const parts: string[] = [];
  while (walker.nextNode()) {
    const text = walker.currentNode.textContent?.trim();
    if (text) parts.push(text);
  }
  return parts.join(' ');
};

export const InsightCarousel = ({
  title,
  count,
  index,
  onIndexChange,
  children,
}: InsightCarouselProps) => {
  const { t } = useTranslation('analysis');
  const titleId = useId();
  const slideRef = useRef<HTMLDivElement>(null);
  const announcerRef = useRef<HTMLDivElement>(null);
  // Only a press on previous/next is announced. The insights are recomputed whenever a filter or
  // the metric changes, and reading the slide on those would talk over the control in use.
  const pagedRef = useRef(false);

  const position = carouselPosition(index, count);

  useEffect(() => {
    if (!pagedRef.current || !slideRef.current || !announcerRef.current) return;
    pagedRef.current = false;
    // Written after render so it carries the new insight's text, which only the consumer knows.
    announcerRef.current.textContent = t('analysisResult.mediaLandscape.pagedAnnouncement', {
      position: position + 1,
      count,
      insight: readableText(slideRef.current),
    });
  }, [position, count, t]);

  if (count === 0) return null;

  const navigation = [
    {
      label: t('analysisResult.mediaLandscape.previousInsight'),
      Icon: ChevronLeftIcon,
      step: count - 1,
    },
    { label: t('analysisResult.mediaLandscape.nextInsight'), Icon: ChevronRightIcon, step: 1 },
  ];

  return (
    <section
      aria-labelledby={titleId}
      aria-roledescription={t('analysisResult.mediaLandscape.carouselRoleDescription')}
      className="flex w-full shrink-0 flex-col gap-3 rounded-lg bg-surface p-4 lg:w-[272px] dark:bg-surface-raised"
    >
      <div className="flex items-center gap-2">
        <LightbulbIcon className="size-[15px] text-foreground" />
        <span id={titleId} className="text-sm font-medium text-foreground">
          {title}
        </span>
        <div className="flex-1" />
        <TooltipProvider>
          {navigation.map(({ label, Icon, step }) => (
            <Tooltip key={label}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={label}
                  className={navButtonClassName}
                  onClick={() => {
                    const next = (position + step) % count;
                    // A lone insight pages onto itself, which re-renders nothing to announce.
                    pagedRef.current = next !== position;
                    onIndexChange(next);
                  }}
                >
                  <Icon className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{label}</TooltipContent>
            </Tooltip>
          ))}
        </TooltipProvider>
      </div>
      <div
        ref={slideRef}
        role="group"
        aria-roledescription={t('analysisResult.mediaLandscape.slideRoleDescription')}
        aria-label={t('analysisResult.mediaLandscape.slidePosition', {
          position: position + 1,
          count,
        })}
        className="flex flex-1 flex-col gap-3"
      >
        {children}
      </div>
      {/* The slide's label already announces the position. */}
      <span aria-hidden="true" className="text-right text-[11px] text-muted-foreground">
        {t('analysisResult.mediaLandscape.insightPosition', { position: position + 1, count })}
      </span>
      {/* Mounted with the carousel and empty until the first page: a live region that appears
          together with its text is not announced. */}
      <div ref={announcerRef} aria-live="polite" aria-atomic="true" className="sr-only" />
    </section>
  );
};
